import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
// @ts-ignore
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";

import authSuccess from "./authSuccess";
import authLink from "./authLink";
import errorLink from "./error";

let apolloClient: ApolloClient<any> | null = null;

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Existing Dictionary field
        Dictionary: {
          read(existing) {
            if (existing) {
              const now = Date.now();
              if (now - existing.timestamp < 3600000) {
                // 1 hour in milliseconds
                return existing.data;
              }
            }
            return undefined;
          },
          merge(existing, incoming) {
            return { data: incoming, timestamp: Date.now() };
          },
        },
        // Add a field policy for all other query fields
        // This ensures all queried fields are cached
        __typename: {
          merge: true,
        },
      },
    },
    Person: {
      fields: {
        roles: {
          merge(existing = [], incoming = [], { readField, mergeObjects }) {
            const getRoleKey = (roleRef: any) => {
              const id = readField("id", roleRef);
              const type = readField("type", roleRef);
              const role = readField("role", roleRef);
              const personId = readField("person_id", roleRef);
              return `${id}-${type}-${role}-${personId}`;
            };

            const roleMap = new Map(
              existing.map((roleRef: any) => [getRoleKey(roleRef), roleRef])
            );

            incoming.forEach((roleRef: any) => {
              const key = getRoleKey(roleRef);
              const existingRole = roleMap.get(key);
              roleMap.set(
                key,
                existingRole ? mergeObjects(existingRole, roleRef) : roleRef
              );
            });

            return Array.from(roleMap.values());
          },
        },
      },
    },
    // Project: {
    //   fields: {
    //     kanban_columns: {
    //       merge(existing, incoming, { readField, variables, args }) {
    //         // Get the project id from the parent object (if available)
    //         // This is a bit tricky, so we fallback to incoming[0]?.projectId if your API provides it
    //         // Otherwise, you may need to pass it via args or variables
    //         const projectId =
    //           incoming && incoming.length > 0 && incoming[0]?.projectId
    //             ? incoming[0].projectId
    //             : args?.positionId || variables?.positionId;

    //         if (!incoming) return existing;
    //         return incoming.map((col) =>
    //           col ? { ...col, projectId: projectId } : col
    //         );
    //       },
    //     },
    //   },
    // },
    // PositionKanbanStep: {
    //   keyFields: ["id", ["projectId"]],
    //   merge(existing, incoming, { args, readField }) {
    //     // Always prefer incoming, but keep project context
    //     return { ...existing, ...incoming };
    //   },
    // },
    // Add a catch-all type policy to ensure all types are cached
    __typename: {
      keyFields: ["id", "person_id", "org_id", "project_id"],
    },
    PersonRole: {
      keyFields: ["id", "type", "role", "person_id"],
    },
  },
});

const httpLink = createUploadLink({
  uri: import.meta.env.VITE_API_URL,
  credentials: "include",
  headers: {
    "apollo-require-preflight": "true",
    client: window.location.hostname.includes("localhost")
      ? `${window.location.protocol}//${window.location.hostname}:${window.location.port}`
      : `https://${window.location.hostname}`,
    authorization: window.location.pathname.includes("share/candidate")
      ? "scp"
      : undefined,
  },
});

async function initializeApolloClient(): Promise<ApolloClient<any>> {
  if (apolloClient) return apolloClient;

  apolloClient = new ApolloClient({
    cache,
    link: ApolloLink.from([authLink, errorLink, authSuccess, httpLink]) as any,
    connectToDevTools: import.meta.env.NODE_ENV === "development",
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first",
        nextFetchPolicy: "cache-first",
      },
      query: {
        fetchPolicy: "cache-first",
      },
      mutate: {
        fetchPolicy: "network-only",
      },
    },
  });

  return apolloClient;
}

export { initializeApolloClient };
